/*
 * MediaPlayer's transport controls (media-controls.scss): the bitmap transport
 * buttons, the seek slider with in/out points, the volume slider, the Mini-mode
 * twistie and the stream buffer bar. Each is a native <button>, <input
 * type="range"> or <progress>; nothing here plays media.
 */

import type { CSSProperties } from "react";
import { type BeProps, beComponent } from "./create.tsx";

/** `zoom` is invented: R5 reaches full screen only through the window's zoom box. It suits
 * a `.be-media-frame`, which takes the video and this strip to full screen together. */
export type BeTransportKind = "rewind" | "stop" | "play" | "forward" | "zoom";

const TRANSPORT_LABELS: Record<BeTransportKind, string> = {
	rewind: "Rewind",
	stop: "Stop",
	play: "Play",
	forward: "Fast forward",
	zoom: "Full screen",
};

export type BeTransportButtonProps = BeProps<
	"button",
	{
		kind: BeTransportKind;
		/** Play only. `paused` blinks the pause LED. */
		playState?: "stopped" | "playing" | "paused";
	}
>;

/** Each state is its own bitmap; a press shows the state the button is about to enter. */
export const BeTransportButton = beComponent<BeTransportButtonProps, HTMLButtonElement>("BeTransportButton", (props, be) => {
	const { kind, playState = "stopped", scale, className, style, type, ...rest } = props;
	const play = kind === "play";
	return (
		<button
			type={type ?? "button"}
			aria-label={TRANSPORT_LABELS[kind]}
			{...rest}
			{...be(
				"be-transport-button",
				`be-transport-button--${kind}`,
				play && playState === "playing" && "is-playing",
				play && playState === "paused" && "is-paused",
			)}
		/>
	);
});

/** HH:MM:SS:ff as DrawText lays it out. R5 takes the last field from microseconds
 * divided by 10, not by 10000, so it is the 10µs digit pair rather than hundredths. */
function beMediaTime(seconds: number): string {
	const us = Math.max(0, Math.floor(seconds * 1e6));
	const s = Math.floor(us / 1e6);
	const pad = (n: number) => String(n % 100).padStart(2, "0");
	return [pad(Math.floor(s / 3600)), pad(Math.floor(s / 60) % 60), pad(s % 60), pad(Math.floor(us / 10))].join(":");
}

export type BeMediaSliderProps = BeProps<
	"input",
	{
		/** Length of the media, in seconds. */
		duration: number;
		/** The playhead, in seconds: the range's value, and what the label shows. */
		position: number;
		/** Start of the play range, in seconds. Default 0. */
		inPoint?: number;
		/** End of the play range, in seconds. Default `duration`. */
		outPoint?: number;
		/** Release the preferred width and span the parent's inline size. */
		fluid?: boolean;
	}
>;

/** A native range whose thumb is the playhead; disabled, the bar reads "Drop files to play".
 * The ref and DOM attributes land on the <input>. */
export const BeMediaSlider = beComponent<BeMediaSliderProps, HTMLInputElement>("BeMediaSlider", (props, be) => {
	const { duration, position, inPoint = 0, outPoint = duration, fluid, onChange, scale, className, style, type: _type, ...rest } = props;
	const at = (t: number) => (duration > 0 ? Math.max(0, Math.min(1, t / duration)) : 0);
	const time = beMediaTime(position);
	const { ref, ...root } = be("be-media-slider", fluid && "is-fluid");
	const vars = {
		...root.style,
		"--be-media-slider-position": at(position),
		"--be-media-slider-in": at(inPoint),
		"--be-media-slider-out": at(outPoint),
	} as CSSProperties;
	return (
		<div {...root} style={vars}>
			<div className="be-media-slider__bar">
				<output className="be-media-slider__label" aria-hidden="true">
					{time}
				</output>
				<div className="be-media-slider__thumb be-media-slider__thumb--in" />
				<div className="be-media-slider__thumb be-media-slider__thumb--out" />
				<input
					type="range"
					className="be-media-slider__input"
					aria-label="Position"
					aria-valuetext={rest.disabled ? "No file" : time}
					min={0}
					max={duration}
					step="any"
					value={position}
					onChange={onChange}
					readOnly={!onChange}
					ref={ref}
					{...rest}
				/>
			</div>
		</div>
	);
});

export type BeVolumeSliderProps = BeProps<"input">;

/** A native range. The fill follows the value through `--be-volume-slider-value`, which the
 * markup sets and this keeps in step while dragging. The ref and DOM attributes land on the <input>. */
export const BeVolumeSlider = beComponent<BeVolumeSliderProps, HTMLInputElement>("BeVolumeSlider", (props, be) => {
	const { min = 0, max = 100, value, defaultValue, onInput, scale, className, style, type: _type, ...rest } = props;
	const { ref, ...root } = be("be-volume-slider");
	const pct = (v: number) => ((Math.max(+min, Math.min(+max, v)) - +min) / (+max - +min)) * 100;
	const initial = Number(value ?? defaultValue ?? (+min + +max) / 2);
	return (
		<div {...root} style={{ ...root.style, "--be-volume-slider-value": pct(initial) } as CSSProperties}>
			<div className="be-volume-slider__bar">
				<input
					type="range"
					className="be-volume-slider__input"
					aria-label="Volume"
					min={min}
					max={max}
					value={value}
					defaultValue={defaultValue}
					onInput={(e) => {
						const el = e.currentTarget;
						el.closest<HTMLElement>(".be-volume-slider")?.style.setProperty("--be-volume-slider-value", String(pct(el.valueAsNumber)));
						onInput?.(e);
					}}
					ref={ref}
					{...rest}
				/>
			</div>
		</div>
	);
});

export type BeKnobSwitchProps = BeProps<
	"button",
	{
		/** Knob down. MediaPlayer's Large mode shows it on. */
		on: boolean;
	}
>;

export const BeKnobSwitch = beComponent<BeKnobSwitchProps, HTMLButtonElement>("BeKnobSwitch", (props, be) => {
	const { on, scale, className, style, type, ...rest } = props;
	return <button type={type ?? "button"} aria-label="Mini mode" aria-pressed={on} {...rest} {...be("be-knob-switch")} />;
});

export type BeBufferBarProps = BeProps<
	"progress",
	{
		/** Release the preferred width and span the parent's inline size. */
		fluid?: boolean;
	}
>;

/** A stream's buffer fill, standing in for the seek slider: a native <progress>. */
export const BeBufferBar = beComponent<BeBufferBarProps, HTMLProgressElement>("BeBufferBar", (props, be) => {
	const { fluid, scale, className, style, ...rest } = props;
	return <progress aria-label="Buffer" max={100} {...rest} {...be("be-buffer-bar", fluid && "is-fluid")} />;
});
